import React, { useEffect, useMemo, useRef } from "react";
import { useGLTF, useTexture, useFBX } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import _ from "lodash";

// Import your animation utilities
import createAnimation from "../converter";
import blinkData from "../blendDataBlink.json";

const Avatar = ({
  avatar_url,
  isSpeaking,
  currentText,
  audioContext,
  audioSource,
}) => {
  const gltf = useGLTF(avatar_url);
  const mixer = useMemo(
    () => new THREE.AnimationMixer(gltf.scene),
    [gltf.scene]
  );

  // Refs for audio analysis and animation
  const analyserRef = useRef(null);
  const prevEnergyRef = useRef(0);
  const animationFrameRef = useRef(null);
  const currentVisemeActionRef = useRef(null);
  const lastAnalysisTimeRef = useRef(0);

  // Enhanced audio analysis state
  const audioStateRef = useRef({
    smoothedAmplitude: 0,
    peakAmplitude: 0,
    amplitudeHistory: new Array(10).fill(0),
    frequencyData: new Float32Array(32),
    prevFrequencyData: new Float32Array(32),
    silenceCounter: 0,
    speechDetected: false,
    vowelWeight: 0,
    consonantWeight: 0,
    currentPhoneme: "silence",
    phonemeConfidence: 0,
    energyHistory: new Array(5).fill(0),
    spectralCentroid: 0,
  });

  // Enhanced lip sync state with more natural parameters
  const lipSyncStateRef = useRef({
    // Current morph weights
    currentMorphs: {
      jawOpen: 0,
      mouthOpen: 0,
      mouthSmileLeft: 0.15, // Base smile
      mouthSmileRight: 0.15,
      mouthPucker: 0,
      mouthWide: 0,
      mouthDimpleLeft: 0,
      mouthDimpleRight: 0,
      mouthStretchLeft: 0,
      mouthStretchRight: 0,
      mouthRollLower: 0,
      mouthRollUpper: 0,
      mouthShrugLower: 0,
      mouthShrugUpper: 0,
      mouthFunnel: 0,
      tongueOut: 0,
      mouthLowerDownLeft: 0,
      mouthLowerDownRight: 0,
      mouthUpperUpLeft: 0,
      mouthUpperUpRight: 0,
    },
    // Target morph weights
    targetMorphs: {},
    // Viseme transition
    visemeBlendFactor: 0.35, // Faster transitions for more responsive movement
    jawBlendFactor: 0.25, // Slightly slower jaw movement for realism
    // Coarticulation
    previousViseme: "silence",
    coarticulationFactor: 0.3,
    // Natural speech patterns
    speechRhythm: 0,
    speechIntensity: 0,
    // Mouth opening calibration
    maxJawOpen: 0.7, // Maximum jaw opening
    maxMouthOpen: 0.8, // Maximum mouth opening
  });

  // Head and neck movement references
  const headNodeRef = useRef(null);
  const neckNodeRef = useRef(null);
  const spineNodeRef = useRef(null);

  // Enhanced blinking state
  const blinkStateRef = useRef({
    nextBlink: Date.now() + Math.random() * 3000 + 1500,
    isBlinking: false,
    blinkProgress: 0,
    blinkSpeed: 1 + Math.random() * 0.3,
    blinkHoldTime: 0.05 + Math.random() * 0.08,
    doubleBlinkChance: 0.18,
    tripleBlinkChance: 0.05,
    partialBlinkChance: 0.15,
    partialBlinkStrength: 0.7,
    lastBlinkTime: 0,
    forcedBlinkCountdown: 7000,
  });

  // Enhanced head movement configuration
  const headMovementRef = useRef({
    baseRotationX: 0,
    baseRotationY: 0,
    baseRotationZ: 0,
    currentLookTarget: new THREE.Vector3(0, 0.1, 0),
    previousLookTarget: new THREE.Vector3(0, 0.1, 0),
    lookTransitionProgress: 1,
    lookTransitionDuration: 1.0,
    microMovementAmplitude: {
      x: 0.012 + Math.random() * 0.008,
      y: 0.004 + Math.random() * 0.003,
    },
    microMovementSpeed: {
      x: 0.6 + Math.random() * 0.4,
      y: 0.5 + Math.random() * 0.4,
    },
    microMovementPhase: {
      x: Math.random() * Math.PI * 2,
      y: Math.random() * Math.PI * 2,
    },
    nextLookChange: Date.now() + Math.random() * 2500 + 1500,
    breathingPhase: Math.random() * Math.PI * 2,
    breathingSpeed: 0.3 + Math.random() * 0.15,
    breathingAmplitude: 0.015 + Math.random() * 0.008,
    prevHeadRotation: new THREE.Vector3(0, 0, 0),
    headVelocity: new THREE.Vector3(0, 0, 0),
    restlessness: Math.random() * 0.6 + 0.6,
    expressiveness: Math.random() * 0.4 + 0.9,
    largeMovementChance: 0.15,
    verticalConstraint: true,
    // Speech-driven head movement
    speechHeadMovement: 0,
    speechHeadMovementDecay: 0.92,
  });

  // Morph target references
  const morphTargetDictionaryRef = useRef({
    body: null,
    lowerTeeth: null,
  });

  // Load idle animation
  const idleFbx = useFBX("/idle15.fbx");
  const { animations: idleAnimations } = idleFbx;

  // Enhanced viseme mapping with proper mouth opening values
  const visemeMap = {
    silence: {
      jawOpen: 0.02,
      mouthOpen: 0.01,
      mouthSmileLeft: 0.15,
      mouthSmileRight: 0.15,
    },
    // Vowels with more pronounced mouth opening
    aa: {
      // "ah" as in "father" - widest mouth opening
      jawOpen: 0.6,
      mouthOpen: 0.7,
      mouthWide: 0.3,
      mouthLowerDownLeft: 0.2,
      mouthLowerDownRight: 0.2,
      mouthUpperUpLeft: 0.1,
      mouthUpperUpRight: 0.1,
    },
    ae: {
      // "a" as in "cat" - wide and slightly stretched
      jawOpen: 0.5,
      mouthOpen: 0.55,
      mouthWide: 0.4,
      mouthStretchLeft: 0.2,
      mouthStretchRight: 0.2,
    },
    ah: {
      // "u" as in "cup" - moderate opening
      jawOpen: 0.4,
      mouthOpen: 0.45,
      mouthWide: 0.15,
    },
    ao: {
      // "aw" as in "caught" - rounded and open
      jawOpen: 0.5,
      mouthOpen: 0.6,
      mouthPucker: 0.2,
      mouthFunnel: 0.15,
      mouthRollLower: 0.1,
    },
    aw: {
      // "ow" as in "cow" - very rounded
      jawOpen: 0.45,
      mouthOpen: 0.5,
      mouthPucker: 0.35,
      mouthFunnel: 0.3,
    },
    ay: {
      // "i" as in "bite" - starts open then closes
      jawOpen: 0.35,
      mouthOpen: 0.4,
      mouthWide: 0.25,
      mouthSmileLeft: 0.2,
      mouthSmileRight: 0.2,
    },
    ee: {
      // "ee" as in "bee" - stretched wide smile
      jawOpen: 0.15,
      mouthOpen: 0.1,
      mouthWide: 0.45,
      mouthSmileLeft: 0.35,
      mouthSmileRight: 0.35,
      mouthStretchLeft: 0.2,
      mouthStretchRight: 0.2,
    },
    eh: {
      // "e" as in "bed" - moderate smile
      jawOpen: 0.3,
      mouthOpen: 0.25,
      mouthWide: 0.3,
      mouthSmileLeft: 0.2,
      mouthSmileRight: 0.2,
    },
    er: {
      // "er" as in "bird" - slight pucker
      jawOpen: 0.25,
      mouthOpen: 0.2,
      mouthPucker: 0.15,
      mouthRollLower: 0.1,
    },
    ih: {
      // "i" as in "bit" - slight smile
      jawOpen: 0.2,
      mouthOpen: 0.15,
      mouthWide: 0.25,
      mouthSmileLeft: 0.15,
      mouthSmileRight: 0.15,
    },
    oh: {
      // "o" as in "go" - rounded
      jawOpen: 0.35,
      mouthOpen: 0.45,
      mouthPucker: 0.4,
      mouthFunnel: 0.3,
      mouthRollLower: 0.15,
      mouthRollUpper: 0.1,
    },
    oo: {
      // "oo" as in "food" - very rounded and puckered
      jawOpen: 0.2,
      mouthOpen: 0.25,
      mouthPucker: 0.6,
      mouthFunnel: 0.5,
      mouthRollLower: 0.2,
      mouthRollUpper: 0.15,
    },
    // Consonants
    m: {
      // Lips pressed together
      jawOpen: 0.01,
      mouthOpen: 0,
      mouthRollLower: 0.3,
      mouthRollUpper: 0.3,
      mouthPucker: 0.1,
    },
    p: {
      // Lips pressed then pop open
      jawOpen: 0.02,
      mouthOpen: 0.01,
      mouthPucker: 0.2,
      mouthRollLower: 0.2,
      mouthRollUpper: 0.2,
    },
    b: {
      // Similar to p but voiced
      jawOpen: 0.05,
      mouthOpen: 0.02,
      mouthPucker: 0.15,
      mouthRollLower: 0.15,
      mouthRollUpper: 0.15,
    },
    f: {
      // Lower lip touches upper teeth
      jawOpen: 0.1,
      mouthOpen: 0.05,
      mouthRollLower: 0.4,
      mouthLowerDownLeft: 0.1,
      mouthLowerDownRight: 0.1,
    },
    v: {
      // Similar to f but voiced
      jawOpen: 0.12,
      mouthOpen: 0.08,
      mouthRollLower: 0.35,
      mouthLowerDownLeft: 0.08,
      mouthLowerDownRight: 0.08,
    },
    th: {
      // Tongue between teeth
      jawOpen: 0.08,
      mouthOpen: 0.15,
      tongueOut: 0.4,
      mouthWide: 0.1,
    },
    s: {
      // Teeth together, slight smile
      jawOpen: 0.05,
      mouthOpen: 0.03,
      mouthWide: 0.15,
      mouthSmileLeft: 0.1,
      mouthSmileRight: 0.1,
    },
    z: {
      // Similar to s but voiced
      jawOpen: 0.08,
      mouthOpen: 0.05,
      mouthWide: 0.12,
      mouthSmileLeft: 0.08,
      mouthSmileRight: 0.08,
    },
    sh: {
      // Lips pushed forward
      jawOpen: 0.12,
      mouthOpen: 0.1,
      mouthPucker: 0.3,
      mouthFunnel: 0.2,
    },
    ch: {
      // Combination of t and sh
      jawOpen: 0.15,
      mouthOpen: 0.12,
      mouthPucker: 0.25,
      mouthFunnel: 0.15,
    },
    k: {
      // Back of tongue raised
      jawOpen: 0.2,
      mouthOpen: 0.15,
      mouthWide: 0.05,
    },
    g: {
      // Similar to k but voiced
      jawOpen: 0.25,
      mouthOpen: 0.2,
      mouthWide: 0.08,
    },
    n: {
      // Tongue touches roof
      jawOpen: 0.1,
      mouthOpen: 0.08,
      mouthWide: 0.05,
    },
    l: {
      // Tongue tip touches roof
      jawOpen: 0.18,
      mouthOpen: 0.15,
      mouthWide: 0.1,
      tongueOut: 0.1,
    },
    r: {
      // Tongue curled back
      jawOpen: 0.15,
      mouthOpen: 0.12,
      mouthPucker: 0.1,
      mouthRollLower: 0.05,
    },
  };

  // Setup audio analyzer when audio context is available
  useEffect(() => {
    if (audioContext && audioSource && isSpeaking) {
      if (!analyserRef.current) {
        analyserRef.current = audioContext.createAnalyser();
        analyserRef.current.fftSize = 2048; // Higher resolution for better frequency analysis
        analyserRef.current.smoothingTimeConstant = 0.3; // Balanced smoothing for responsiveness
      }

      try {
        audioSource.connect(analyserRef.current);
        analyserRef.current.connect(audioContext.destination);
      } catch (error) {
        console.error("Error connecting audio nodes:", error);
      }

      return () => {
        try {
          audioSource.disconnect(analyserRef.current);
          analyserRef.current.disconnect();
        } catch (error) {
          console.error("Error disconnecting audio nodes:", error);
        }
      };
    }
  }, [audioContext, audioSource, isSpeaking]);

  // Setup materials, morphs and find head node
  useEffect(() => {
    let headFound = false;
    let neckFound = false;
    let spineFound = false;

    gltf.scene.traverse((node) => {
      if (
        node.type === "Mesh" ||
        node.type === "LineSegments" ||
        node.type === "SkinnedMesh"
      ) {
        node.castShadow = true;
        node.receiveShadow = true;
        node.frustumCulled = false;

        if (node.name.includes("Wolf3D_Avatar")) {
          morphTargetDictionaryRef.current.body = node.morphTargetDictionary;
          console.log("Available morph targets:", node.morphTargetDictionary);
        }

        if (node.name.includes("TeethLower")) {
          morphTargetDictionaryRef.current.lowerTeeth =
            node.morphTargetDictionary;
        }
      }

      // Find bones for natural movement
      if (node.type === "Bone") {
        if (node.name.includes("Head") && !headFound) {
          headNodeRef.current = node;
          headMovementRef.current.baseRotationX = node.rotation.x;
          headMovementRef.current.baseRotationY = node.rotation.y;
          headMovementRef.current.baseRotationZ = node.rotation.z;
          headMovementRef.current.prevHeadRotation.set(
            node.rotation.x,
            node.rotation.y,
            node.rotation.z
          );
          headFound = true;
        } else if (node.name.includes("Neck") && !neckFound) {
          neckNodeRef.current = node;
          neckFound = true;
        } else if (node.name.includes("Spine2") && !spineFound) {
          spineNodeRef.current = node;
          spineFound = true;
        }
      }
    });

    // Initialize lip sync morphs
    Object.keys(lipSyncStateRef.current.currentMorphs).forEach((key) => {
      lipSyncStateRef.current.targetMorphs[key] =
        lipSyncStateRef.current.currentMorphs[key];
    });

    blinkStateRef.current.nextBlink = Date.now() + Math.random() * 4000 + 2000;
  }, [gltf.scene]);

  // Enhanced audio analysis for natural lip sync
  const analyzeAudio = () => {
    if (!analyserRef.current || !isSpeaking) return null;

    const audioState = audioStateRef.current;
    const bufferLength = analyserRef.current.frequencyBinCount;
    const frequencyData = new Uint8Array(bufferLength);
    const timeData = new Uint8Array(bufferLength);

    analyserRef.current.getByteFrequencyData(frequencyData);
    analyserRef.current.getByteTimeDomainData(timeData);

    // Calculate RMS amplitude for more accurate volume detection
    let rms = 0;
    for (let i = 0; i < timeData.length; i++) {
      const normalized = (timeData[i] - 128) / 128;
      rms += normalized * normalized;
    }
    rms = Math.sqrt(rms / timeData.length);

    // Track amplitude history for better peak detection
    audioState.amplitudeHistory.shift();
    audioState.amplitudeHistory.push(rms);

    // Calculate smoothed amplitude with peak detection
    const avgAmplitude =
      audioState.amplitudeHistory.reduce((a, b) => a + b) /
      audioState.amplitudeHistory.length;
    audioState.smoothedAmplitude =
      audioState.smoothedAmplitude * 0.6 + avgAmplitude * 0.4;
    audioState.peakAmplitude = Math.max(...audioState.amplitudeHistory);

    // Enhanced frequency band analysis for phoneme detection
    const freqBands = {
      subBass: 0, // 0-60 Hz
      bass: 0, // 60-250 Hz
      lowMid: 0, // 250-500 Hz
      mid: 0, // 500-2000 Hz
      upperMid: 0, // 2000-4000 Hz
      presence: 0, // 4000-6000 Hz
      brilliance: 0, // 6000+ Hz
    };

    // Calculate frequency bands (assuming 44.1kHz sample rate)
    const hzPerBin = 44100 / 2 / bufferLength;
    for (let i = 0; i < bufferLength; i++) {
      const freq = i * hzPerBin;
      const magnitude = frequencyData[i] / 255;

      if (freq < 60) freqBands.subBass += magnitude;
      else if (freq < 250) freqBands.bass += magnitude;
      else if (freq < 500) freqBands.lowMid += magnitude;
      else if (freq < 2000) freqBands.mid += magnitude;
      else if (freq < 4000) freqBands.upperMid += magnitude;
      else if (freq < 6000) freqBands.presence += magnitude;
      else freqBands.brilliance += magnitude;
    }

    // Normalize frequency bands
    Object.keys(freqBands).forEach((band) => {
      freqBands[band] = freqBands[band] / 20; // Rough normalization
    });

    // Calculate spectral centroid for brightness detection
    let weightedSum = 0;
    let magnitudeSum = 0;
    for (let i = 0; i < bufferLength / 2; i++) {
      const magnitude = frequencyData[i];
      weightedSum += i * magnitude;
      magnitudeSum += magnitude;
    }
    audioState.spectralCentroid =
      magnitudeSum > 0 ? weightedSum / magnitudeSum / bufferLength : 0;

    // Detect speech characteristics
    const isSilent = audioState.smoothedAmplitude < 0.015;
    audioState.silenceCounter = isSilent ? audioState.silenceCounter + 1 : 0;
    audioState.speechDetected = !isSilent && audioState.silenceCounter < 5;

    // Enhanced vowel/consonant detection based on formants
    const f1 = freqBands.bass + freqBands.lowMid; // First formant (tongue height)
    const f2 = freqBands.mid + freqBands.upperMid; // Second formant (tongue position)
    const highFreqEnergy = freqBands.presence + freqBands.brilliance;

    // Phoneme classification based on formant analysis
    let detectedPhoneme = "silence";
    let confidence = 0;

    if (audioState.speechDetected) {
      // Check for consonants first (they have distinctive features)
      if (highFreqEnergy > 0.4 && audioState.smoothedAmplitude < 0.1) {
        // Fricatives (s, sh, f)
        if (audioState.spectralCentroid > 0.4) {
          detectedPhoneme = "s";
          confidence = 0.7;
        } else {
          detectedPhoneme = "sh";
          confidence = 0.6;
        }
      } else if (
        audioState.smoothedAmplitude < 0.02 &&
        audioState.peakAmplitude > 0.1
      ) {
        // Plosives (p, b, k)
        detectedPhoneme = "p";
        confidence = 0.5;
      } else if (f1 > 0.5 && f2 < 0.3) {
        // Low vowels (aa, ah)
        detectedPhoneme = "aa";
        confidence = 0.8;
      } else if (f1 < 0.2 && f2 > 0.6) {
        // High front vowels (ee, ih)
        detectedPhoneme = "ee";
        confidence = 0.8;
      } else if (f1 < 0.3 && f2 < 0.3) {
        // High back vowels (oo, uh)
        detectedPhoneme = "oo";
        confidence = 0.7;
      } else if (f1 > 0.3 && f2 > 0.4) {
        // Mid vowels (eh, ae)
        detectedPhoneme = "eh";
        confidence = 0.6;
      } else {
        // Default to open vowel for general speech
        detectedPhoneme = "ah";
        confidence = 0.5;
      }
    }

    audioState.currentPhoneme = detectedPhoneme;
    audioState.phonemeConfidence = confidence;

    // Update speech rhythm and intensity
    lipSyncStateRef.current.speechRhythm =
      lipSyncStateRef.current.speechRhythm * 0.85 +
      audioState.smoothedAmplitude * 0.15;

    lipSyncStateRef.current.speechIntensity = Math.max(
      0,
      Math.min(1, audioState.peakAmplitude * 4)
    );

    return {
      amplitude: audioState.smoothedAmplitude,
      peakAmplitude: audioState.peakAmplitude,
      phoneme: detectedPhoneme,
      confidence: confidence,
      speechDetected: audioState.speechDetected,
      freqBands: freqBands,
      spectralCentroid: audioState.spectralCentroid,
    };
  };

  // Generate natural viseme based on audio analysis with coarticulation
  const generateNaturalViseme = (audioData) => {
    if (!audioData || !audioData.speechDetected) {
      return visemeMap.silence;
    }

    const { amplitude, peakAmplitude, phoneme, confidence, freqBands } =
      audioData;
    const lipSync = lipSyncStateRef.current;

    // Get base viseme for detected phoneme
    let targetViseme = visemeMap[phoneme] || visemeMap.ah;

    // Apply coarticulation (blend with previous viseme)
    const prevViseme = visemeMap[lipSync.previousViseme] || visemeMap.silence;
    const coarticulatedViseme = {};

    Object.keys(targetViseme).forEach((key) => {
      const targetValue = targetViseme[key] || 0;
      const prevValue = prevViseme[key] || 0;
      // Blend based on confidence and coarticulation factor
      coarticulatedViseme[key] =
        prevValue * (1 - confidence) * lipSync.coarticulationFactor +
        targetValue * (1 - lipSync.coarticulationFactor * (1 - confidence));
    });

    // Dynamic amplitude-based scaling for more natural movement
    const amplitudeScale = 0.6 + Math.pow(peakAmplitude, 0.7) * 0.4;
    const dynamicViseme = {};

    Object.keys(coarticulatedViseme).forEach((key) => {
      let value = coarticulatedViseme[key];

      // Special handling for jaw and mouth opening - make them more responsive
      if (key === "jawOpen" || key === "mouthOpen") {
        // Use peak amplitude for more dynamic mouth opening
        value = value * (0.7 + peakAmplitude * 0.3);

        // Add extra opening for loud speech
        if (peakAmplitude > 0.3) {
          value = Math.min(
            value * 1.2,
            key === "jawOpen" ? lipSync.maxJawOpen : lipSync.maxMouthOpen
          );
        }
      } else {
        // Other morphs scale with smoothed amplitude
        value = value * amplitudeScale;
      }

      dynamicViseme[key] = value;
    });

    // Add natural asymmetry and micro-movements
    const time = Date.now() * 0.001;
    const microMovement = Math.sin(time * 3.7) * 0.02 * amplitude;

    // Asymmetric smile based on speech rhythm
    dynamicViseme.mouthSmileLeft =
      (dynamicViseme.mouthSmileLeft || 0.15) +
      lipSync.speechRhythm * 0.05 +
      microMovement;
    dynamicViseme.mouthSmileRight =
      (dynamicViseme.mouthSmileRight || 0.15) +
      lipSync.speechRhythm * 0.05 -
      microMovement;

    // Add subtle mouth corner movements for realism
    if (amplitude > 0.1) {
      dynamicViseme.mouthDimpleLeft = Math.sin(time * 2.3) * 0.03 * amplitude;
      dynamicViseme.mouthDimpleRight = Math.sin(time * 2.7) * 0.03 * amplitude;

      // Add slight mouth stretch during loud speech
      if (peakAmplitude > 0.25) {
        dynamicViseme.mouthStretchLeft =
          (dynamicViseme.mouthStretchLeft || 0) + 0.05;
        dynamicViseme.mouthStretchRight =
          (dynamicViseme.mouthStretchRight || 0) + 0.05;
      }
    }

    // Update previous phoneme for next frame's coarticulation
    lipSync.previousViseme = phoneme;

    return dynamicViseme;
  };

  // Apply morph targets with smooth transitions and proper mouth opening
  const applyMorphTargets = (targetMorphs, deltaTime) => {
    const lipSync = lipSyncStateRef.current;

    // Update target morphs
    Object.keys(targetMorphs).forEach((key) => {
      lipSync.targetMorphs[key] = targetMorphs[key];
    });

    // Smoothly transition current morphs to targets with different speeds for different morphs
    Object.keys(lipSync.currentMorphs).forEach((key) => {
      const target = lipSync.targetMorphs[key] || 0;
      const current = lipSync.currentMorphs[key];

      // Use different blend speeds for different morph types
      let blendFactor = lipSync.visemeBlendFactor;

      if (key === "jawOpen" || key === "mouthOpen") {
        // Jaw and mouth opening need to be responsive but smooth
        blendFactor = lipSync.jawBlendFactor;
      } else if (key.includes("Smile") || key.includes("Dimple")) {
        // Smile morphs should be slower for natural expression
        blendFactor = lipSync.visemeBlendFactor * 0.7;
      } else if (key.includes("Blink")) {
        // Don't interfere with blink morphs
        return; // Use return instead of continue in forEach
      }

      // Use different speeds for opening vs closing
      const speed = target > current ? blendFactor * 1.3 : blendFactor * 0.9;

      // Apply easing for more natural movement
      const diff = target - current;
      const easedDiff = diff * speed;

      lipSync.currentMorphs[key] = current + easedDiff;

      // Clamp values to reasonable ranges
      lipSync.currentMorphs[key] = Math.max(
        0,
        Math.min(1, lipSync.currentMorphs[key])
      );
    });

    gltf.scene.traverse((node) => {
      if (node.morphTargetInfluences && node.morphTargetDictionary) {
        // Apply all available morph targets
        Object.keys(lipSync.currentMorphs).forEach((morphName) => {
          if (node.morphTargetDictionary[morphName] !== undefined) {
            const morphIndex = node.morphTargetDictionary[morphName];
            node.morphTargetInfluences[morphIndex] =
              lipSync.currentMorphs[morphName];
          }
        });

        // Handle legacy/alternative morph target names
        // Some avatars use different naming conventions
        const morphMappings = {
          mouthSmile: ["mouthSmileLeft", "mouthSmileRight"],
          mouthOpen: ["mouthOpenFront"],
          jawOpen: ["jawForward", "jawDown"],
        };

        Object.entries(morphMappings).forEach(
          ([standardName, alternativeNames]) => {
            if (lipSync.currentMorphs[standardName] !== undefined) {
              alternativeNames.forEach((altName) => {
                if (node.morphTargetDictionary[altName] !== undefined) {
                  const morphIndex = node.morphTargetDictionary[altName];
                  node.morphTargetInfluences[morphIndex] =
                    lipSync.currentMorphs[standardName];
                }
              });
            }
          }
        );

        // Special handling for mouth open to ensure it's visible
        if (
          node.morphTargetDictionary.mouthOpen !== undefined &&
          lipSync.currentMorphs.mouthOpen > 0
        ) {
          const mouthOpenIndex = node.morphTargetDictionary.mouthOpen;
          // Amplify mouth opening for better visibility
          node.morphTargetInfluences[mouthOpenIndex] = Math.min(
            1,
            lipSync.currentMorphs.mouthOpen * 1.2
          );
        }

        // Ensure jaw movement is applied
        if (
          node.morphTargetDictionary.jawOpen !== undefined &&
          lipSync.currentMorphs.jawOpen > 0
        ) {
          const jawOpenIndex = node.morphTargetDictionary.jawOpen;
          // Amplify jaw opening for better visibility
          node.morphTargetInfluences[jawOpenIndex] = Math.min(
            1,
            lipSync.currentMorphs.jawOpen * 1.15
          );
        }
      }
    });
  };

  // Enhanced natural blinking pattern
  const updateBlinking = (deltaTime) => {
    const now = Date.now();
    const blinkState = blinkStateRef.current;

    if (
      !blinkState.isBlinking &&
      now - blinkState.lastBlinkTime > blinkState.forcedBlinkCountdown
    ) {
      blinkState.isBlinking = true;
      blinkState.blinkProgress = 0;
      blinkState.blinkSpeed = 1.2 + Math.random() * 0.4;
    } else if (!blinkState.isBlinking && now > blinkState.nextBlink) {
      blinkState.isBlinking = true;
      blinkState.blinkProgress = 0;

      const isPartialBlink = Math.random() < blinkState.partialBlinkChance;
      blinkState.partialBlinkAmount = isPartialBlink
        ? Math.random() * 0.3 + blinkState.partialBlinkStrength * 0.7
        : 1.0;

      const speedVariations = [0.8, 1.0, 1.2, 1.5];
      const speedIndex = Math.floor(Math.random() * speedVariations.length);
      blinkState.blinkSpeed = speedVariations[speedIndex] + Math.random() * 0.4;
    }

    if (blinkState.isBlinking) {
      blinkState.blinkProgress += deltaTime * 5 * blinkState.blinkSpeed;

      let blinkValueLeft = 0;
      let blinkValueRight = 0;
      const asymmetry =
        blinkState.partialBlinkAmount === 1.0 ? Math.random() * 0.1 - 0.05 : 0;

      if (blinkState.blinkProgress < 1) {
        const closeEase = Math.sin(blinkState.blinkProgress * Math.PI * 0.5);
        blinkValueLeft = closeEase * blinkState.partialBlinkAmount;
        blinkValueRight = closeEase * blinkState.partialBlinkAmount + asymmetry;
      } else if (blinkState.blinkProgress < 1 + blinkState.blinkHoldTime) {
        blinkValueLeft = blinkState.partialBlinkAmount;
        blinkValueRight = blinkState.partialBlinkAmount + asymmetry;
      } else if (blinkState.blinkProgress < 2 + blinkState.blinkHoldTime) {
        const openProgress =
          blinkState.blinkProgress - 1 - blinkState.blinkHoldTime;
        const openEase = 1 - Math.sin(openProgress * Math.PI * 0.5);
        blinkValueLeft = openEase * blinkState.partialBlinkAmount;
        blinkValueRight =
          openEase * blinkState.partialBlinkAmount +
          asymmetry * (1 - openProgress);
      } else {
        blinkState.isBlinking = false;
        blinkValueLeft = 0;
        blinkValueRight = 0;
        blinkState.lastBlinkTime = now;

        const activityFactor = isSpeaking ? 0.7 : 1.0;
        const baseBlinksPerMinute = 15;
        const randomVariation = Math.random() * 0.5 + 0.75;
        const msPerBlink =
          (60000 / baseBlinksPerMinute) * activityFactor * randomVariation;
        blinkState.nextBlink = now + msPerBlink;

        const multiBlinkRandom = Math.random();
        if (multiBlinkRandom < blinkState.doubleBlinkChance) {
          blinkState.nextBlink = now + 150 + Math.random() * 150;
        } else if (
          multiBlinkRandom <
          blinkState.doubleBlinkChance + blinkState.tripleBlinkChance
        ) {
          blinkState.nextBlink = now + 150 + Math.random() * 100;
        }
      }

      gltf.scene.traverse((node) => {
        if (node.morphTargetInfluences && node.morphTargetDictionary) {
          if (typeof node.morphTargetDictionary.eyeBlinkLeft !== "undefined") {
            node.morphTargetInfluences[
              node.morphTargetDictionary.eyeBlinkLeft
            ] = blinkValueLeft;
          }
          if (typeof node.morphTargetDictionary.eyeBlinkRight !== "undefined") {
            node.morphTargetInfluences[
              node.morphTargetDictionary.eyeBlinkRight
            ] = blinkValueRight;
          }
        }
      });
    }
  };

  // Enhanced head movements with speech-driven motion
  const updateHeadMovements = (deltaTime, time) => {
    if (!headNodeRef.current) return;

    const headState = headMovementRef.current;
    const now = Date.now();

    // Add subtle head movement based on speech intensity
    if (isSpeaking && lipSyncStateRef.current.speechIntensity > 0.1) {
      // More pronounced head movement during energetic speech
      headState.speechHeadMovement =
        lipSyncStateRef.current.speechIntensity * 0.03 +
        Math.sin(time * 2.5) * 0.01 * lipSyncStateRef.current.speechIntensity;
    } else {
      headState.speechHeadMovement *= headState.speechHeadMovementDecay;
    }

    // Major head movement changes
    if (
      headState.lookTransitionProgress >= 1 &&
      now > headState.nextLookChange
    ) {
      headState.previousLookTarget.copy(headState.currentLookTarget);
      const isListening = !isSpeaking;
      const expressiveness = headState.expressiveness;
      const isLargeMovement = Math.random() < headState.largeMovementChance;

      let horizontalRange, verticalRange;
      if (isLargeMovement) {
        horizontalRange = isListening
          ? 0.35 * expressiveness
          : 0.25 * expressiveness;
        verticalRange = 0.05 * expressiveness;
      } else {
        horizontalRange = isListening
          ? 0.25 * expressiveness
          : 0.15 * expressiveness;
        verticalRange = 0.035 * expressiveness;
      }

      const gaussianRandom = () => {
        return (
          ((Math.random() + Math.random() + Math.random() + Math.random()) / 4 -
            0.5) *
          2
        );
      };

      const shouldTilt = Math.random() < 0.3;
      const tiltAmount = shouldTilt ? Math.random() * 0.1 - 0.05 : 0;
      const directionalBias =
        Math.random() < 0.4 ? (Math.random() > 0.5 ? 0.5 : -0.5) : 0;
      const targetX =
        (gaussianRandom() + directionalBias * 0.3) * horizontalRange;

      let targetY;
      if (headState.verticalConstraint) {
        const rawY = gaussianRandom();
        if (rawY > 0) {
          targetY = 0.1 + rawY * verticalRange * 0.2;
        } else {
          targetY = 0.1 + rawY * verticalRange * 0.8;
        }
      } else {
        targetY = gaussianRandom() * verticalRange + 0.05;
      }

      if (isListening && Math.random() < 0.5) {
        const cameraVariance = 0.015;
        headState.currentLookTarget.set(
          Math.random() * cameraVariance * 2 - cameraVariance,
          0.1 + Math.random() * cameraVariance * 0.5 - cameraVariance * 0.3,
          0
        );
      } else {
        headState.currentLookTarget.set(targetX, targetY, tiltAmount);
      }

      headState.lookTransitionProgress = 0;
      const distance = headState.previousLookTarget.distanceTo(
        headState.currentLookTarget
      );
      headState.lookTransitionDuration =
        0.8 + distance * 2 + Math.random() * 0.5;

      const restlessnessFactor = headState.restlessness;
      const nextChangeDelay = isSpeaking
        ? 5000 - restlessnessFactor * 2000 + Math.random() * 3000
        : 3000 - restlessnessFactor * 1500 + Math.random() * 2000;
      headState.nextLookChange = now + nextChangeDelay;
    }

    if (headState.lookTransitionProgress < 1) {
      headState.lookTransitionProgress +=
        deltaTime / headState.lookTransitionDuration;
      headState.lookTransitionProgress = Math.min(
        headState.lookTransitionProgress,
        1
      );
    }

    const easeNatural = (t) => {
      if (t < 0.5) {
        return 0.5 * (1 - Math.cos(Math.PI * t));
      } else {
        return 0.5 + 0.5 * Math.sin(((t - 0.5) * Math.PI) / 2);
      }
    };

    const t = easeNatural(headState.lookTransitionProgress);
    const interpolatedX =
      headState.previousLookTarget.x +
      (headState.currentLookTarget.x - headState.previousLookTarget.x) * t;
    const interpolatedY =
      headState.previousLookTarget.y +
      (headState.currentLookTarget.y - headState.previousLookTarget.y) * t;

    const microX1 =
      Math.sin(
        time * headState.microMovementSpeed.x + headState.microMovementPhase.x
      ) * headState.microMovementAmplitude.x;
    const microY1 =
      Math.sin(
        time * headState.microMovementSpeed.y + headState.microMovementPhase.y
      ) * headState.microMovementAmplitude.y;

    const microX2 =
      Math.sin(time * 2.1 + headState.microMovementPhase.x * 1.3) *
      headState.microMovementAmplitude.x *
      0.3;
    const microY2 =
      Math.sin(time * 1.7 + headState.microMovementPhase.y * 1.7) *
      headState.microMovementAmplitude.y *
      0.3;

    const breathingMotion =
      Math.sin(time * headState.breathingSpeed + headState.breathingPhase) *
      headState.breathingAmplitude;

    // Add speech-driven motion
    const speechMotionY = Math.sin(time * 3.2) * headState.speechHeadMovement;
    const speechMotionX =
      Math.cos(time * 2.8) * headState.speechHeadMovement * 0.5;

    const finalX = interpolatedX + microX1 + microX2 + speechMotionX;
    const finalY = interpolatedY + microY1 + microY2 + speechMotionY;

    const currentHeadPos = new THREE.Vector3(
      headState.baseRotationX + finalY,
      headState.baseRotationY + finalX,
      headState.baseRotationZ
    );

    headState.headVelocity
      .subVectors(currentHeadPos, headState.prevHeadRotation)
      .multiplyScalar(1 / deltaTime)
      .multiplyScalar(0.2);

    const inertiaFactor = 0.92;
    const inertiaInfluence = 0.03;

    const inertiaX = headState.headVelocity.y * inertiaInfluence;
    const inertiaY = headState.headVelocity.x * inertiaInfluence;

    headState.prevHeadRotation.copy(currentHeadPos);

    const tiltZ = headState.currentLookTarget.z || 0;

    if (headNodeRef.current) {
      headNodeRef.current.rotation.x =
        headState.baseRotationX + finalY * 0.8 + inertiaY;
      headNodeRef.current.rotation.y =
        headState.baseRotationY + finalX * 1.2 + inertiaX;
      headNodeRef.current.rotation.z =
        headState.baseRotationZ + tiltZ + microX1 * 0.2;
    }

    if (neckNodeRef.current) {
      neckNodeRef.current.rotation.x = finalY * 0.4 + breathingMotion;
      neckNodeRef.current.rotation.y = finalX * 0.7;
      neckNodeRef.current.rotation.z = tiltZ * 0.3;
    }

    if (spineNodeRef.current) {
      spineNodeRef.current.rotation.x = breathingMotion;
      spineNodeRef.current.rotation.y = finalX * 0.2;
      spineNodeRef.current.rotation.z = tiltZ * 0.1;
    }
  };

  // Animation update loop
  useFrame((state, deltaTime) => {
    const time = state.clock.getElapsedTime();

    // Update natural head movements
    updateHeadMovements(deltaTime, time);

    // Update natural blinking
    updateBlinking(deltaTime);

    // Handle lip sync for speaking
    if (isSpeaking && analyserRef.current) {
      // Analyze audio
      const audioData = analyzeAudio();

      if (audioData) {
        // Generate natural viseme based on audio
        const targetViseme = generateNaturalViseme(audioData);

        // Apply morphs with smooth transitions
        applyMorphTargets(targetViseme, deltaTime);
      }
    } else if (!isSpeaking) {
      // Return to rest position when not speaking
      const restViseme = {
        mouthOpen: 0.01,
        jawOpen: 0.02,
        mouthSmileLeft: 0.15,
        mouthSmileRight: 0.15,
        mouthPucker: 0,
        mouthWide: 0,
        mouthDimpleLeft: 0,
        mouthDimpleRight: 0,
        mouthStretchLeft: 0,
        mouthStretchRight: 0,
        mouthRollLower: 0,
        mouthRollUpper: 0,
        mouthShrugLower: 0,
        mouthShrugUpper: 0,
        mouthFunnel: 0,
        tongueOut: 0,
        mouthLowerDownLeft: 0,
        mouthLowerDownRight: 0,
        mouthUpperUpLeft: 0,
        mouthUpperUpRight: 0,
      };

      // Gradually return to rest pose
      applyMorphTargets(restViseme, deltaTime);
    }

    // Update animation mixer
    mixer.update(deltaTime);
  });

  // Setup idle animation
  useEffect(() => {
    if (idleAnimations?.[0]) {
      const idleClip = idleAnimations[0].clone();
      idleClip.tracks = idleClip.tracks.filter((track) => {
        return (
          !track.name.includes("Head") &&
          !track.name.includes("Neck") &&
          track.name.includes("Spine")
        );
      });

      const action = mixer.clipAction(idleClip);
      action.play();

      return () => {
        action.stop();
      };
    }
  }, [mixer, idleAnimations]);

  return (
    <group name="avatar">
      <primitive object={gltf.scene} dispose={null} />
    </group>
  );
};

export default Avatar;
